import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import moment from "moment";
import { getOnboardingConfig } from "../../services/onboarding.api";
import {
  getContact360Summary,
  getContact360Timeline,
  uploadContactPicture,
  updateContact,
  updateContactProfile360,
} from "../../services/contacts.api";
import { i18n } from "../../translate/i18n";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import { PROFILE_PATCH_KEYS, REFERRAL_SOURCE_OPTIONS } from "./contact360.constants";
import { buildPaymentMethodOptions, isBeautyBusinessProfile, normalizeNotasInternasLogFromProfile } from "./contact360.helpers";

// TIMELINE_PAGE + emptyHistorialState (movidos del index; solo los usa esta lógica)
const TIMELINE_PAGE = 25;

function emptyHistorialState() {
  return {
    events: [],
    iaHints: [],
    total: 0,
    loading: false,
    loadingMore: false,
    timelineRestrictedMsg: null
  };
}

// D8.4.5 — Lógica (16 estados + effects + handlers + API) de ContactProfile360Modal (hook cerebro).
export default function useContactProfile360({ open, onClose, contactId, onContactUpdated, reloadContactSignal = 0 }) {
  const mountedRef = React.useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const [tabIndex, setTabIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [summary, setSummary] = useState(null);
  const [onboardingConfig, setOnboardingConfig] = useState(null);
  const [draft, setDraft] = useState({});
  const [tagDraft, setTagDraft] = useState("");
  const [notaInput, setNotaInput] = useState("");
  const [savingExclude, setSavingExclude] = useState(false);
  const [historial, setHistorial] = useState(() => emptyHistorialState());

  /** Columnas `Contacts`: nombre, WhatsApp y correo (PUT /contacts, no sólo perfil JSON). */
  const [identity, setIdentity] = useState({ name: "", number: "", email: "" });
  const [avatarKey, setAvatarKey] = useState(0);
  const [avatarLightboxOpen, setAvatarLightboxOpen] = useState(false);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [selectedCropSource, setSelectedCropSource] = useState(null);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const pictureInputRef = useRef(null);

  const loadSummary = useCallback(async () => {
    if (!contactId) return;
    try {
      setLoading(true);
      const { data } = await getContact360Summary(contactId);
      if (!mountedRef.current) return;
      const pd = data?.contact?.profileData || {};
      const nextDraft = {};
      PROFILE_PATCH_KEYS.forEach((k) => {
        const v = pd[k];
        nextDraft[k] =
          v != null && v !== "" && typeof v !== "object" ? String(v) : "";
      });
      nextDraft.contactTags = Array.isArray(pd.contactTags)
        ? [...pd.contactTags]
            .map((x) => (typeof x === "string" ? x.trim() : ""))
            .filter(Boolean)
        : [];
      nextDraft.excludeFromSequences = !!pd.excludeFromSequences;
      nextDraft.notasInternasLog = normalizeNotasInternasLogFromProfile(pd);
      setDraft(nextDraft);
      setSummary(data);
      const c = data?.contact;
      setIdentity({
        name: c?.name != null ? String(c.name) : "",
        number: c?.number != null ? String(c.number) : "",
        email: c?.email != null ? String(c.email) : ""
      });
      setTagDraft("");
      setNotaInput("");
    } catch (e) {
      toastError(e);
      setSummary(null);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [contactId]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await getOnboardingConfig();
        if (!mountedRef.current || cancelled) return;
        setOnboardingConfig(data && typeof data === "object" ? data : {});
      } catch {
        if (!mountedRef.current || cancelled) return;
        setOnboardingConfig({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const fetchTimelinePage = useCallback(
    async (reset) => {
      if (!contactId) return;
      let offset = 0;
      setHistorial((prev) => {
        offset = reset ? 0 : prev.events.length;
        return {
          ...prev,
          loading: !!reset,
          loadingMore: !reset
        };
      });
      try {
        const { data } = await getContact360Timeline(contactId, {
          params: { limit: TIMELINE_PAGE, offset }
        });
        if (!mountedRef.current) return;
        const nextEvents = Array.isArray(data.events) ? data.events : [];
        const nextTotal = typeof data.total === "number" ? data.total : 0;
        setHistorial((prev) => ({
          ...prev,
          loading: false,
          loadingMore: false,
          events: reset ? nextEvents : [...prev.events, ...nextEvents],
          iaHints: reset && Array.isArray(data.iaHints) ? data.iaHints : prev.iaHints,
          total: nextTotal,
          timelineRestrictedMsg:
            data.permissions?.restrictedMessage != null
              ? String(data.permissions.restrictedMessage)
              : null
        }));
      } catch (e) {
        if (!mountedRef.current) return;
        toastError(e);
        setHistorial((prev) => ({
          ...prev,
          loading: false,
          loadingMore: false
        }));
      }
    },
    [contactId]
  );

  useEffect(() => {
    if (!open) return;
    setTabIndex(0);
    setHistorial(emptyHistorialState());
    setOnboardingConfig(null);
    loadSummary();
  }, [open, loadSummary]);

  useEffect(() => {
    if (!open || !contactId || !reloadContactSignal) return;
    loadSummary();
  }, [reloadContactSignal, open, contactId, loadSummary]);

  useEffect(() => {
    if (!open || tabIndex !== 2 || !contactId) return;
    fetchTimelinePage(true);
  }, [open, tabIndex, contactId, fetchTimelinePage]);

  useEffect(() => {
    if (open) return;
    setCropModalOpen(false);
    setUploadingPicture(false);
    if (pictureInputRef.current) pictureInputRef.current.value = "";
    setSelectedCropSource((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, [open]);

  const handleProfilePicturePick = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/gif"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Solo imágenes JPG, PNG, WEBP o GIF.");
      event.target.value = "";
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Máximo 5 MB.");
      event.target.value = "";
      return;
    }
    if (!contactId) return;
    setSelectedCropSource((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
    setCropModalOpen(true);
  };

  const handleCropModalClose360 = () => {
    setCropModalOpen(false);
    setSelectedCropSource((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    if (pictureInputRef.current) pictureInputRef.current.value = "";
  };

  const handleProfilePictureCropped = async (croppedImageUrl) => {
    setCropModalOpen(false);
    setUploadingPicture(true);
    try {
      if (
        !croppedImageUrl ||
        (!croppedImageUrl.startsWith("blob:") && !croppedImageUrl.startsWith("data:"))
      ) {
        toast.error("Imagen inválida. Intenta de nuevo.");
        handleCropModalClose360();
        setUploadingPicture(false);
        return;
      }

      let response;
      try {
        response = await fetch(croppedImageUrl);
      } catch {
        toast.error("No se pudo leer la imagen recortada (CSP/red).");
        setUploadingPicture(false);
        return;
      }
      if (!response.ok) throw new Error(`Fetch imagen ${response.status}`);

      const blob = await response.blob();
      const file = new File([blob], "profile-picture.jpg", { type: "image/jpeg" });
      const formData = new FormData();
      formData.append("picture", file);

      const { data } = await uploadContactPicture(contactId, formData);

      toast.success(data?.message || "Foto actualizada.");
      setAvatarKey((n) => n + 1);
      await loadSummary();
      onContactUpdated?.();
    } catch (err) {
      toastError(err);
    } finally {
      if (mountedRef.current) setUploadingPicture(false);
      setSelectedCropSource((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      if (pictureInputRef.current) pictureInputRef.current.value = "";
    }
  };

  const contact = summary?.contact;
  const kpis = summary?.kpis || {};
  const permissions = summary?.permissions || {};
  const aiExtractedData = contact?.aiExtractedData || {};
  const appointments = Array.isArray(summary?.appointments) ? summary.appointments : [];
  const tags = Array.isArray(draft.contactTags) ? draft.contactTags : [];

  const whatsappConnName = contact?.whatsapp?.name || null;

  const isBeautyProfile = useMemo(() => isBeautyBusinessProfile(onboardingConfig), [onboardingConfig]);

  const serviceSelectOptions = useMemo(() => {
    const svc = onboardingConfig?.services;
    if (!Array.isArray(svc)) return [];
    return svc.map((s) => ({
      value: String(s.id),
      label: String(s.serviceName || s.name || `#${s.id}`)
    }));
  }, [onboardingConfig]);

  const professionalSelectOptions = useMemo(() => {
    const pr = onboardingConfig?.professionals;
    if (!Array.isArray(pr)) return [];
    return pr.map((p) => ({
      value: String(p.id),
      label: String(p.name || `#${p.id}`)
    }));
  }, [onboardingConfig]);

  const paymentSelectOptions = useMemo(() => buildPaymentMethodOptions(onboardingConfig || {}), [onboardingConfig]);

  const serviceIdSet = useMemo(() => new Set(serviceSelectOptions.map((o) => o.value)), [serviceSelectOptions]);
  const professionalIdSet = useMemo(
    () => new Set(professionalSelectOptions.map((o) => o.value)),
    [professionalSelectOptions]
  );

  const referralCodeSet = useMemo(() => new Set(REFERRAL_SOURCE_OPTIONS.map((o) => o.value)), []);

  const legacyServicioLabel =
    draft.servicioFavorito &&
    String(draft.servicioFavorito).trim() !== "" &&
    !serviceIdSet.has(String(draft.servicioFavorito))
      ? String(draft.servicioFavorito)
      : null;

  const legacyProfesionalLabel =
    draft.profesional &&
    String(draft.profesional).trim() !== "" &&
    !professionalIdSet.has(String(draft.profesional))
      ? String(draft.profesional)
      : null;

  const legacyReferralLabel =
    draft.comoNosConocio &&
    String(draft.comoNosConocio).trim() !== "" &&
    !referralCodeSet.has(String(draft.comoNosConocio).toLowerCase())
      ? String(draft.comoNosConocio)
      : null;

  const paymentCodeSet = useMemo(() => new Set(paymentSelectOptions.map((o) => String(o.value))), [paymentSelectOptions]);

  const legacyMetodoPagoLabel =
    draft.metodoPago &&
    String(draft.metodoPago).trim() !== "" &&
    !paymentCodeSet.has(String(draft.metodoPago))
      ? String(draft.metodoPago)
      : null;

  const formattedProxima = useMemo(() => {
    const dt = kpis?.proximaCita?.appointmentDate;
    if (!dt) return "Sin registrar";
    return moment(dt).format("DD/MM HH:mm");
  }, [kpis]);

  const freqLabel =
    kpis.frecuenciaDiasPromedio != null && kpis.frecuenciaDiasPromedio > 0
      ? `${kpis.frecuenciaDiasPromedio} d`
      : "Sin registrar";

  const valorLabel =
    typeof kpis.valorTotal === "number"
      ? `${kpis.valorMoneda || "S/ "} ${kpis.valorTotal.toFixed(2)}`
      : "Sin registrar";

  const handleDraftChange = (key, raw) => {
    setDraft((prev) => ({ ...prev, [key]: raw }));
  };

  const mergeNewTags = (extra) => {
    const base = Array.isArray(draft.contactTags) ? [...draft.contactTags] : [];
    const seen = new Set(base.map((t) => String(t).trim().toLowerCase()).filter(Boolean));
    extra.forEach((t) => {
      const n = String(t).trim().toLowerCase();
      if (!n || seen.has(n)) return;
      seen.add(n);
      base.push(t.trim());
    });
    setDraft((prev) => ({ ...prev, contactTags: base.slice(0, 10) }));
  };

  const addTagsFromInput = () => {
    const parts = tagDraft
      .split(/[,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    mergeNewTags(parts);
    setTagDraft("");
  };

  const removeTag = (tag) => {
    setDraft((prev) => ({
      ...prev,
      contactTags: (prev.contactTags || []).filter((t) => t !== tag)
    }));
  };

  const appendNotaInterna = () => {
    const t = notaInput.trim();
    if (t.length < 3) {
      toast.error("La nota debe tener al menos 3 caracteres");
      return;
    }
    setDraft((prev) => ({
      ...prev,
      notasInternasLog: [
        ...(Array.isArray(prev.notasInternasLog) ? prev.notasInternasLog : []),
        { text: t, createdAt: new Date().toISOString() }
      ]
    }));
    setNotaInput("");
  };

  const handleSaveProfile = async () => {
    if (!contactId) return;
    const name = identity.name.trim();
    const numberDigits = identity.number.replace(/\D/g, "");
    const email = identity.email.trim();
    if (name.length < 2 || name.length > 50) {
      toast.error("El nombre debe tener entre 2 y 50 caracteres.");
      return;
    }
    if (numberDigits.length < 8 || numberDigits.length > 50) {
      toast.error("El número de WhatsApp debe tener entre 8 y 50 dígitos.");
      return;
    }
    if (email !== "" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("El correo no es válido.");
      return;
    }

    const profilePatch = {};
    PROFILE_PATCH_KEYS.forEach((k) => {
      const v = draft[k];
      profilePatch[k] = v === "" || v == null ? null : v;
    });
    profilePatch.contactTags = Array.isArray(draft.contactTags) ? [...draft.contactTags] : [];
    profilePatch.excludeFromSequences = !!draft.excludeFromSequences;

    profilePatch.notasInternasLog = Array.isArray(draft.notasInternasLog)
      ? draft.notasInternasLog.map((x) =>
          x && typeof x === "object"
            ? {
                text: String(x.text || "").trim(),
                createdAt: typeof x.createdAt === "string" ? x.createdAt : undefined,
                userId: typeof x.userId === "number" ? x.userId : undefined
              }
            : null
        )
      : [];
    profilePatch.notasInternasLog = profilePatch.notasInternasLog.filter((x) => x && x.text);
    profilePatch.notasInternas = null;
    profilePatch.conexionOrigen = null;

    try {
      setSaving(true);
      const identityRes = await updateContact(contactId, {
        name,
        number: numberDigits,
        email
      });

      if (identityRes?.data?.warning === "NUMBER_NOT_IN_WHATSAPP") {
        toast.warning(
          identityRes.data.message ||
            i18n.t("backendErrors.NUMBER_NOT_IN_WHATSAPP") ||
            "Contacto guardado; el número no está registrado en WhatsApp.",
          { autoClose: 5000 }
        );
      }

      const savedRow = identityRes?.data;
      if (savedRow && mountedRef.current) {
        setIdentity({
          name: savedRow.name != null ? String(savedRow.name) : name,
          number: savedRow.number != null ? String(savedRow.number) : numberDigits,
          email: savedRow.email != null ? String(savedRow.email) : email
        });
      }

      await updateContactProfile360(contactId, { profilePatch });
      if (!mountedRef.current) return;
      toast.success("Contacto y perfil guardados");
      await loadSummary();
      onContactUpdated?.();
    } catch (e) {
      toastError(e);
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  };

  const persistExcludeFromSequences = async (next) => {
    if (!contactId) return;
    try {
      setSavingExclude(true);
      await updateContactProfile360(contactId, { profilePatch: { excludeFromSequences: next } });
      if (!mountedRef.current) return;
      toast.success("Preferencia de secuencias actualizada");
      await loadSummary();
      onContactUpdated?.();
    } catch (e) {
      toastError(e);
    } finally {
      if (mountedRef.current) setSavingExclude(false);
    }
  };

  const restricted = !!permissions.restrictedMessage;

  return {
    addTagsFromInput,
    aiExtractedData,
    appendNotaInterna,
    appointments,
    avatarKey,
    avatarLightboxOpen,
    contact,
    cropModalOpen,
    draft,
    fetchTimelinePage,
    formattedProxima,
    freqLabel,
    handleCropModalClose360,
    handleDraftChange,
    handleProfilePictureCropped,
    handleProfilePicturePick,
    handleSaveProfile,
    historial,
    identity,
    isBeautyProfile,
    kpis,
    legacyMetodoPagoLabel,
    legacyProfesionalLabel,
    legacyReferralLabel,
    legacyServicioLabel,
    loadSummary,
    loading,
    mergeNewTags,
    mountedRef,
    notaInput,
    onboardingConfig,
    paymentCodeSet,
    paymentSelectOptions,
    permissions,
    persistExcludeFromSequences,
    pictureInputRef,
    professionalIdSet,
    professionalSelectOptions,
    referralCodeSet,
    removeTag,
    restricted,
    saving,
    savingExclude,
    selectedCropSource,
    serviceIdSet,
    serviceSelectOptions,
    setAvatarKey,
    setAvatarLightboxOpen,
    setCropModalOpen,
    setDraft,
    setHistorial,
    setIdentity,
    setLoading,
    setNotaInput,
    setOnboardingConfig,
    setSaving,
    setSavingExclude,
    setSelectedCropSource,
    setSummary,
    setTabIndex,
    setTagDraft,
    setUploadingPicture,
    summary,
    tabIndex,
    tagDraft,
    tags,
    uploadingPicture,
    valorLabel,
    whatsappConnName,
  };
}
